import React, { useState, useEffect } from 'react';
import api from '../api';
import './AdminDashboard.css';
import { ADMIN_PASSWORD } from '../config.js';

const AdminDashboard = () => {
    const [password, setPassword] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [data, setData] = useState({ userStats: [], queries: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('users');

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            console.log('Attempting to fetch data...');
            const response = await api.get('/api/queries');
            const videoGenerationsResponse = await await api.getVideoGenerations();

            console.log('Fetched data queries:', response.data);
            console.log('Fetched data video generations:', videoGenerationsResponse.data);
            setData({
                userStats: response.data.userStats || [],
                queries: response.data.queries || [],
                videoGenerations: videoGenerationsResponse.data || []
            });
        } catch (error) {
            console.error('Detailed error:', error.response || error);
            setError(error.response?.data?.error || error.message || 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated) {
            fetchData();
        }
    }, [isAuthenticated]);

    const handleSubmit = (e) => {
        console.log(password);
        console.log('Expected password:', ADMIN_PASSWORD);
        e.preventDefault();
        if (password === ADMIN_PASSWORD) {
            setIsAuthenticated(true);
        } else {
            alert('Incorrect password');
        }
    };

    if (!isAuthenticated) {
        return (
            <div className="password-container">
                <form onSubmit={handleSubmit} className="password-form">
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter password"
                        className="password-input"
                    />
                    <button type="submit" className="submit-button">
                        Submit
                    </button>
                </form>
            </div>
        );
    }

    if (loading) return <div className="loading">Loading...</div>;
    if (error) return <div className="error">Error: {error}</div>;

    return (
        <div className="admin-dashboard">
            <h1>Admin Dashboard</h1>

            <div className="tabs">
                <button 
                    className={activeTab === 'users' ? 'active' : ''} 
                    onClick={() => setActiveTab('users')}
                >
                    Users
                </button>
                <button 
                    className={activeTab === 'queries' ? 'active' : ''} 
                    onClick={() => setActiveTab('queries')}
                >
                    Queries
                </button>
                <button 
                    className={activeTab === 'videos' ? 'active' : ''} 
                    onClick={() => setActiveTab('videos')}
                >
                    Videos
                </button>
            </div>
            
            {activeTab === 'users' && (
                <section className="user-stats">
                    <h2>User Statistics</h2>
                    <table>
                        <thead style={{ color: 'black' }}>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Documents</th>
                                <th>Queries</th>
                            </tr>
                        </thead>
                        <tbody style={{ color: 'black' }}>
                            {data.userStats?.map((user) => (
                                <tr key={user._id}>
                                    <td>{user.name}</td>
                                    <td>{user.email}</td>
                                    <td>{user.documentCount}</td>
                                    <td>{user.queryCount}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            )}

            {activeTab === 'queries' && (
                <section className="recent-queries">
                    <h2>Recent Queries</h2>
                    <div className="queries-list">
                        {data.queries?.map((query) => (
                            <div key={query._id} className="query-card">
                                <div className="query-header">
                                    <span className="user-info">
                                        {query.userId?.name} ({query.userId?.email})
                                    </span>
                                    <span className="timestamp">
                                        {new Date(query.createdAt).toLocaleString()}
                                    </span>
                                </div>
                                <div className="query-content">
                                    <p className="question"><strong>Q:</strong> {query.question}</p>
                                    <p className="answer"><strong>A:</strong> {query.answer}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {activeTab === 'videos' && (
                <section className="video-generations">
                    <h2>Video Generations</h2>
                    <div className="video-list" style={{ color: 'black' }}>
                        {data.videoGenerations?.map((video) => (
                            <div key={video._id} className="video-card">
                                <div className="video-header">
                                    <span className="user-info">
                                        {video.userId?.name} ({video.userId?.email})
                                    </span>
                                    <span className="timestamp">
                                        {new Date(video.createdAt).toLocaleString()}
                                    </span>
                                </div>
                                <div className="video-details">
                                    <p><strong>Source:</strong> {video.sourceType} ({video.originalFilename})</p>
                                    <p><strong>Model:</strong> {video.modelUsed}</p>
                                    <p><strong>Credits:</strong> {video.creditsCost}</p>
                                    <p>
                                        <strong>Status: </strong> 
                                        <span className={`status-${video.status}`}>{video.status}</span>
                                    </p>
                                    <p><strong>Interests:</strong> {video.selectedInterests.join(', ')}</p>
                                    <p><strong>Prompt:</strong> {video.prompt}</p>
                                    {video.error && (
                                        <p className="error-message">
                                            <strong>Error:</strong> {video.error}
                                        </p>
                                    )}
                                </div>
                                <div className="video-comparison">
                                    {video.sourceUrl ? (
                                        <div className="video-preview source-video">
                                            <h4>Input {video.sourceType}</h4>
                                            {video.sourceType === 'video' ? (
                                                <video 
                                                    controls 
                                                    width="100%" 
                                                    height="auto"
                                                    preload="metadata"
                                                    playsInline
                                                    crossOrigin="anonymous"
                                                >
                                                    <source src={video.sourceUrl} type="video/mp4" />
                                                    Your browser does not support the video tag.
                                                </video>
                                            ) : (
                                                <img src={video.sourceUrl} alt="Source" style={{ maxWidth: '100%' }} />
                                            )}
                                        </div>
                                    ) : (
                                        <div className="video-preview source-video">
                                            <h4>Input {video.sourceType}</h4>
                                            <p>Source file not available</p>
                                        </div>
                                    )}
                                    
                                    {video.generatedVideoUrl ? (
                                        <div className="video-preview generated-video">
                                            <h4>Generated Video</h4>
                                            <video controls width="100%">
                                                <source src={video.generatedVideoUrl} type="video/mp4" />
                                                Your browser does not support the video tag.
                                            </video>
                                        </div>
                                    ) : video.status === 'processing' ? (
                                        <div className="video-preview generated-video processing">
                                            <h4>Generated Video</h4>
                                            <div className="processing-indicator">
                                                <div className="spinner"></div>
                                                <p>Processing...</p>
                                            </div>
                                        </div>
                                    ) : video.status === 'failed' ? (
                                        <div className="video-preview generated-video failed">
                                            <h4>Generated Video</h4>
                                            <div className="failed-indicator">
                                                <p>Generation failed</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="video-preview generated-video">
                                            <h4>Generated Video</h4>
                                            <p>No video generated yet</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
};

export default AdminDashboard; 